import { useQueries } from "@tanstack/react-query";
import { SARFINDER_BE_API_URL } from "utils/constants";

const MAX_FAILURES = 8;

// A request the backend never answers (a gunicorn worker stuck behind a heavy
// tile render, say) would otherwise stay pending for good: React Query only
// retries a rejected request, so the route loader spun until a full page
// reload. Aborting it turns the hang into a failure that gets retried.
const REQUEST_TIMEOUT_MS = 15000;

function fetchJson(url, querySignal) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  querySignal?.addEventListener("abort", () => controller.abort());
  return fetch(url, { credentials: "include", signal: controller.signal })
    .then((res) => {
      if (!res.ok) {
        throw new Error("Network response was not ok");
      }
      return res.json();
    })
    .finally(() => clearTimeout(timer));
}

export function useUser() {
  const queries = useQueries({
    queries: [
      {
        queryKey: ["auth:csrf"],
        queryFn: ({ signal }) =>
          fetchJson(`${SARFINDER_BE_API_URL}/accounts/csrf/`, signal),
        retry: (failureCount) => failureCount < MAX_FAILURES,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000),
        refetchOnMount: true,
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        staleTime: 1 * (60 * 1000),
      },
      {
        queryKey: ["auth:user"],
        queryFn: ({ signal }) =>
          fetchJson(`${SARFINDER_BE_API_URL}/accounts/me/`, signal),
        retry: (failureCount) => failureCount < MAX_FAILURES,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000),
        refetchOnMount: true,
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        // * Consider data to be "fresh" for 1 minute.
        staleTime: 1 * (60 * 1000),
      },
    ],
  });

  // Directly return the userQuery object
  return queries[1]; // This is the userQuery
}
